import { setPasswordSchema, type Copyright, type SetPasswordInput } from '@smarton-pbx/shared';
import { ArrowRight } from 'lucide-react';
import { type FormEvent, useState } from 'react';
import { Link } from 'react-router';
import { Field, Notice } from '../components/ui';
import { t } from '../i18n';
import { useSubmit } from '../lib/hooks';
import { AuthLayout } from './AuthLayout';

/** Opened from a one-time link: https://<host>/set-password#token=… */
export function SetPassword({
  name,
  logoUrl,
  copyright,
  setPassword,
}: {
  name: string;
  logoUrl?: string | null;
  copyright?: Copyright | null;
  setPassword: (input: SetPasswordInput) => Promise<unknown>;
}) {
  const token = new URLSearchParams(window.location.hash.slice(1)).get('token') ?? '';
  const [password, setPasswordValue] = useState('');
  const [repeat, setRepeat] = useState('');
  const [done, setDone] = useState(false);
  const form = useSubmit();

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (password !== repeat) {
      form.setError(t('The two passwords are different.'));
      return;
    }
    if (await form.submit(setPasswordSchema, { token, password }, async (input) => (await setPassword(input), true))) {
      // Don't leave the token in the address bar or history.
      window.history.replaceState(null, '', '/set-password');
      setDone(true);
    }
  }

  return (
    <AuthLayout name={name} logoUrl={logoUrl} copyright={copyright}>
      <div className="auth-title">
        <h1>{done ? t('You’re all set') : t('Set your password')}</h1>
        <p>{done ? t('Your password is saved. Sign in to continue.') : t('Choose a password for {name}.', { name })}</p>
      </div>
      {done ? (
        <>
          <Notice kind="success">{t('Your password is set.')}</Notice>
          <Link className="button" to="/" style={{ height: 42 }}>
            {t('Sign in')} <ArrowRight size={16} aria-hidden />
          </Link>
        </>
      ) : !token ? (
        <Notice kind="error">{t('This link is incomplete. Open the full link you were given.')}</Notice>
      ) : (
        <form className="stack" onSubmit={submit} noValidate>
          <Field label={t('New password')} hint={t('At least 12 characters. A few unrelated words work well.')} error={form.fields.password}>
            <input
              type="password"
              autoComplete="new-password"
              value={password}
              onChange={(e) => setPasswordValue(e.target.value)}
              autoFocus
              data-testid="new-password"
            />
          </Field>
          <Field label={t('Repeat it')}>
            <input type="password" autoComplete="new-password" value={repeat} onChange={(e) => setRepeat(e.target.value)} data-testid="repeat-password" />
          </Field>
          {form.error && <Notice kind="error">{form.error}</Notice>}
          <button type="submit" disabled={form.busy} data-testid="save-password" style={{ height: 42 }}>
            {t('Save password')}
          </button>
        </form>
      )}
    </AuthLayout>
  );
}
